import type { Campaign, GlobalSettings } from './types';

/**
 * Campaign fixtures — 3 active programs for Alignment Health Plan.
 * Per Cycle 1 directive: FOBT, BP CUFFS, A1c & KED.
 * Adds one archived campaign for the "past season" state.
 */
export const ALIGNMENT_CAMPAIGNS: Campaign[] = [
  {
    id: '25fobt-2026-q1',
    code: 'COL01-2026',
    nickname: 'FOBT 2026 Q1',
    measure: 'COL · Colorectal Cancer Screening (HEDIS)',
    method: 'FOBT · fecal occult blood test · at-home kit',
    launch: '2026-04-15',
    endDate: '2026-08-15',
    cohort: 3892,
    owner: 'sarah.chen@exampleplan.com',
    approver: 'cmo@exampleplan.com',
    approvedOn: '2026-03-28',
    active: true,
    archived: false,
    eligibility: {
      ageBand: '45–75',
      lob: ['MA', 'D-SNP'],
      states: ['CA', 'AZ', 'NV'],
      exclusions: [
        'Colonoscopy in past 10 years',
        'Sigmoidoscopy in past 5 years',
        'FIT/FOBT in past 12 months',
        'Hospice or palliative status',
        'Active GI bleed (180d)',
      ],
      fieldSchema: [],
      eligibilityFile: { filename: '', source: '', received: '', format: '', rows: 0, sizeBytes: 0, sha256: '', encryption: '', submittedBy: '', validatedBy: '' },
      validation: [],
      validationLastRun: '',
    },
    materials: {
      videoScreens: [
        { title: 'Why iFOB · English', caption: 'English · 90s educational clip', imageFilename: '', videoFilename: 'fobt-explainer-en-90s.mp4', audioFilename: '' },
        { title: 'Why iFOB · Spanish', caption: 'Spanish · 90s educational clip', imageFilename: '', videoFilename: 'fobt-explainer-es-90s.mp4', audioFilename: '' },
        { title: 'Why iFOB · Short',   caption: 'English · 60s short cut',         imageFilename: '', videoFilename: 'fobt-explainer-en-60s.mp4', audioFilename: '' },
      ],
      audioCards: [
        { title: 'Cover 2026 Q1',        caption: 'Member-facing landing card.',           imageFilename: 'cover-2026-q1.png',      videoFilename: '', audioFilename: '' },
        { title: 'Eligibility (Spanish)', caption: 'Spanish-language eligibility tile.',   imageFilename: 'eligibility-spanish.png', videoFilename: '', audioFilename: '' },
        { title: 'Compliance tile v3',    caption: 'Compliance verification step.',        imageFilename: 'compliance-tile-v3.png',  videoFilename: '', audioFilename: '' },
      ],
      quickSurvey: [
        { title: 'Kit insert 2026',      caption: '',  imageFilename: '', videoFilename: '', audioFilename: '' },
        { title: 'Reminder postcard v2', caption: '',  imageFilename: '', videoFilename: '', audioFilename: '' },
        { title: 'Lab req form',         caption: '',  imageFilename: '', videoFilename: '', audioFilename: '' },
      ],
    },
    kit: {
      vendor: 'Polymedco',
      sku: 'OC-AUTO-2026',
      returnMethod: 'Prepaid USPS · Polymedco lab Brewster NY',
      contents: [
        'Collection vial × 1 (sealed)',
        'Sample card (bilingual instructions)',
        'Return envelope (USPS prepaid)',
        'Quickstart insert (4-panel fold)',
        'PCP attribution card',
      ],
    },
    comms: {
      schedule: '11-touch cadence over 56 days',
      languages: ['English', 'Spanish'],
      channels: ['Mail', 'SMS', 'IVR', 'Live'],
      autoSend: [],
      optIn: [],
    },
    waves: [
      { id: 'W1', date: '2026-04-15', kits: 1300, status: 'shipped' },
      { id: 'W2', date: '2026-05-06', kits: 1300, status: 'shipped' },
      { id: 'W3', date: '2026-05-27', kits: 1292, status: 'scheduled' },
    ],
  },
  {
    id: '25a1c',
    code: 'HBD01-2026',
    nickname: 'A1c & KED',
    measure: 'HBD · Hemoglobin A1c Control + KED · Kidney Health (HEDIS)',
    method: 'A1c at-home test + UACR urine sample (bundled)',
    launch: '2026-06-01',
    endDate: '2026-11-30',
    cohort: 1247,
    owner: 'marisol.alvarez@exampleplan.com',
    approver: 'cmo@exampleplan.com',
    approvedOn: '2026-05-09',
    active: true,
    archived: false,
    eligibility: {
      ageBand: '18–75',
      lob: ['MA', 'D-SNP'],
      states: ['CA'],
      fieldSchema: [],
      eligibilityFile: { filename: '', source: '', received: '', format: '', rows: 0, sizeBytes: 0, sha256: '', encryption: '', submittedBy: '', validatedBy: '' },
      validation: [],
      validationLastRun: '',
      exclusions: [
        'Type 1 diabetes diagnosed prior to age 30',
        'Recent A1c on file (within 90d)',
        'End-stage renal disease (KED satisfied via dialysis)',
        'Hospice or palliative status',
      ],
    },
    materials: {
      videoScreens: [
        { title: 'A1c Explainer · English', caption: 'English · explainer clip', imageFilename: '', videoFilename: 'a1c-explainer-en.mp4', audioFilename: '' },
        { title: 'A1c Explainer · Spanish', caption: 'Spanish · explainer clip', imageFilename: '', videoFilename: 'a1c-explainer-es.mp4', audioFilename: '' },
      ],
      audioCards: [
        { title: 'A1c Cover',         caption: 'A1c program intro card.',  imageFilename: 'a1c-cover.png',         videoFilename: '', audioFilename: '' },
        { title: 'KED Overview Tile', caption: 'KED measure overview.',    imageFilename: 'ked-overview-tile.png', videoFilename: '', audioFilename: '' },
      ],
      quickSurvey: [
        { title: 'Kit Insert 2026', caption: '', imageFilename: '', videoFilename: '', audioFilename: '' },
        { title: 'Lab Req Form',    caption: '', imageFilename: '', videoFilename: '', audioFilename: '' },
      ],
    },
    kit: {
      vendor: 'Siemens Healthineers',
      sku: 'DCA-A1C-UACR-2026',
      returnMethod: 'Prepaid USPS · Siemens Newark lab',
      contents: [
        'A1c capillary blood collector × 1',
        'UACR urine collection cup × 1',
        'Return mailer (USPS prepaid)',
        'Quickstart insert (bilingual)',
        'Lab requisition form',
      ],
    },
    comms: {
      schedule: '9-touch cadence over 45 days',
      languages: ['English', 'Spanish'],
      channels: ['Mail', 'SMS', 'IVR'],
      autoSend: [],
      optIn: [],
    },
    waves: [
      { id: 'W1', date: '2026-06-01', kits: 624, status: 'scheduled' },
      { id: 'W2', date: '2026-06-22', kits: 623, status: 'awaiting_approval' },
    ],
  },
  {
    id: '25ked',
    code: 'KED01-2025',
    nickname: 'KED 2025 - Test',
    measure: 'KED · Kidney Health Evaluation for Diabetes (HEDIS)',
    method: 'eGFR blood test + UACR urine test',
    launch: '2025-09-01',
    endDate: '2025-12-31',
    cohort: 10326,
    owner: '',
    approver: '',
    approvedOn: '',
    active: true,
    archived: false,
    eligibility: {
      ageBand: '18–85',
      lob: ['MA'],
      states: ['AZ', 'CA', 'NV'],
      fieldSchema: [],
      eligibilityFile: { filename: '', source: '', received: '', format: '', rows: 0, sizeBytes: 0, sha256: '', encryption: '', submittedBy: '', validatedBy: '' },
      validation: [],
      validationLastRun: '',
      exclusions: [
        'End-stage renal disease (on dialysis — KED satisfied)',
        'Hospice or palliative status',
      ],
    },
    materials: {
      videoScreens: [],
      audioCards: [],
      quickSurvey: [],
    },
    kit: {
      vendor: '',
      sku: '',
      returnMethod: '',
      contents: [],
    },
    comms: {
      schedule: '',
      languages: ['English', 'Spanish'],
      channels: ['Mail', 'SMS', 'IVR'],
      autoSend: [],
      optIn: [],
    },
    waves: [],
  },
  {
    id: '25a1cked',
    code: 'HBDKED01-2025',
    nickname: 'A1C + KED Combined - Test',
    measure: 'HBD + KED · Hemoglobin A1c Control + Kidney Health Evaluation for Diabetes (HEDIS)',
    method: 'A1c at-home test + UACR urine + eGFR blood (bundled)',
    launch: '2025-09-01',
    endDate: '2025-12-31',
    cohort: 0,
    owner: '',
    approver: '',
    approvedOn: '',
    active: true,
    archived: false,
    eligibility: {
      ageBand: '18–75',
      lob: ['MA'],
      states: ['AZ', 'CA', 'NV'],
      fieldSchema: [],
      eligibilityFile: { filename: '', source: '', received: '', format: '', rows: 0, sizeBytes: 0, sha256: '', encryption: '', submittedBy: '', validatedBy: '' },
      validation: [],
      validationLastRun: '',
      exclusions: [
        'Type 1 diabetes diagnosed prior to age 30',
        'End-stage renal disease (KED satisfied via dialysis)',
        'Hospice or palliative status',
      ],
    },
    materials: { videoScreens: [], audioCards: [], quickSurvey: [] },
    kit: { vendor: '', sku: '', returnMethod: '', contents: [] },
    comms: {
      schedule: '',
      languages: ['English', 'Spanish'],
      channels: ['Mail', 'SMS', 'IVR'],
      autoSend: [],
      optIn: [],
    },
    waves: [],
  },
  {
    id: '26fobt',
    code: '26fobt',
    nickname: 'FOBT 2026',
    measure: 'COL · Colorectal Cancer Screening (HEDIS)',
    method: 'FOBT · fecal occult blood test · at-home kit',
    launch: '',
    endDate: '',
    cohort: 0,
    owner: '',
    approver: '',
    approvedOn: '',
    active: true,
    archived: false,
    eligibility: {
      ageBand: '45–75',
      lob: ['MA', 'D-SNP'],
      states: ['CA', 'AZ', 'NV'],
      exclusions: [
        'Colonoscopy in past 10 years',
        'Sigmoidoscopy in past 5 years',
        'FIT/FOBT in past 12 months',
        'Hospice or palliative status',
      ],
      fieldSchema: [],
      eligibilityFile: { filename: '', source: '', received: '', format: '', rows: 0, sizeBytes: 0, sha256: '', encryption: '', submittedBy: '', validatedBy: '' },
      validation: [],
      validationLastRun: '',
    },
    materials: { videoScreens: [], audioCards: [], quickSurvey: [] },
    kit: { vendor: '', sku: '', returnMethod: '', contents: [] },
    comms: {
      schedule: '',
      languages: ['English', 'Spanish'],
      channels: ['Mail', 'SMS', 'IVR'],
      autoSend: [],
      optIn: [],
    },
    waves: [],
  },
  {
    id: '25fobt-2025-fall',
    code: 'COL02-2025',
    nickname: 'FOBT 2025 Fall',
    measure: 'COL · Colorectal Cancer Screening (HEDIS)',
    method: 'FOBT · fecal occult blood test · at-home kit',
    launch: '2025-09-15',
    endDate: '2026-01-15',
    cohort: 3214,
    owner: 'sarah.chen@exampleplan.com',
    approver: 'cmo@exampleplan.com',
    approvedOn: '2025-08-28',
    active: false,
    archived: true,
    eligibility: {
      ageBand: '45–75',
      lob: ['MA'],
      states: ['CA'],
      exclusions: ['Colonoscopy in past 10 years', 'Sigmoidoscopy in past 5 years'],
      fieldSchema: [],
      eligibilityFile: { filename: '', source: '', received: '', format: '', rows: 0, sizeBytes: 0, sha256: '', encryption: '', submittedBy: '', validatedBy: '' },
      validation: [],
      validationLastRun: '',
    },
    materials: {
      videoScreens: [
        { title: 'Why iFOB · English', caption: 'English · 90s educational clip', imageFilename: '', videoFilename: 'fobt-explainer-en-90s.mp4', audioFilename: '' },
      ],
      audioCards: [
        { title: 'Cover 2025 Q1', caption: 'Past-season landing card.', imageFilename: 'cover-2025-q1.png', videoFilename: '', audioFilename: '' },
      ],
      quickSurvey: [
        { title: 'Kit Insert 2025',      caption: '', imageFilename: '', videoFilename: '', audioFilename: '' },
        { title: 'Reminder Postcard v1', caption: '', imageFilename: '', videoFilename: '', audioFilename: '' },
      ],
    },
    kit: {
      vendor: 'Polymedco',
      sku: 'OC-AUTO-2025',
      returnMethod: 'Prepaid USPS · Polymedco lab',
      contents: ['Collection vial × 1', 'Sample card', 'Return envelope', 'Insert'],
    },
    comms: {
      schedule: '8-touch cadence over 42 days',
      languages: ['English'],
      channels: ['Mail', 'SMS'],
      autoSend: [],
      optIn: [],
    },
    waves: [{ id: 'W1', date: '2025-09-15', kits: 3214, status: 'closed' }],
  },
];

/** Global Settings fixture — pairs with ALIGNMENT_CAMPAIGNS for offline / fixture mode.
 * The seed mirrors the initial rev-15 Users list in 00 Global Setup.xlsx. */
export const ALIGNMENT_GLOBAL: GlobalSettings = {
  watermarkMode: 1,
  watermark: { url: '', filename: '' },
  demoMode: false,
  siteDisplayName: '',
  supportEmail: '',
  supportPhone: '',
  campaignFolders: [],
  users: [
    { email: 'william.faidi@claridynamics.com', name: 'Faidi, William',  initials: 'WF' },
    { email: 'alex.bol@claridynamics.com',      name: 'Bol, Alex',       initials: 'AB' },
    { email: 'sherry.east@claridynamics.com',   name: 'East, Sherry',    initials: 'SE' },
    { email: 'andre.mariano@claridynamics.com', name: 'Mariano, Andre',  initials: 'AM' },
    { email: 'jp.silva@claridynamics.com',      name: 'Silva, JP',       initials: 'JS' },
    { email: 'jason.taber@claridynamics.com',   name: 'Taber, Jason',    initials: 'JT' },
    { email: 'sarah.warnock@claridynamics.com', name: 'Warnock, Sarah',  initials: 'SW' },
  ],
};
